/* =====================================================================
   Single source of truth for pricing plans.
   Consumed by both the homepage teaser (#pricing) and the full /pricing
   page so prices and plan details can never drift between them.
   ===================================================================== */

export type PlanId = 'free' | 'starter' | 'team';

export interface Plan {
  id: PlanId;
  name: string;
  /** Short "who it's for" line — used as the blurb on both surfaces. */
  recommendedFor: string;
  /** Base monthly price (0 for Free). Team is priced via pricePerUser. */
  priceMonthly: number;
  pricePerUser?: number;
  minimumSeats?: number;
  billingInterval: 'month';
  isPopular?: boolean;
  badge?: string;
  ctaLabel: string;
  /** Full feature model shown on /pricing (highlights + limits combined). */
  highlights: string[];
  limits: string[];
  /** Curated, shorter bullet list shown in the homepage teaser. */
  teaser: string[];
}

export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    recommendedFor: 'Individuals trying PabSync',
    priceMonthly: 0,
    billingInterval: 'month',
    ctaLabel: 'Start free',
    highlights: ['1 project', 'Task list + backlog', 'Basic Kanban'],
    limits: [
      'Slack integration (single workspace)',
      'Community / self-help support',
    ],
    teaser: [
      '1 project',
      'Task list + backlog',
      'Basic Kanban',
      'Slack (single workspace)',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    recommendedFor: 'Solo power users',
    priceMonthly: 5,
    billingInterval: 'month',
    isPopular: true,
    badge: 'Most popular',
    ctaLabel: 'Choose Starter',
    highlights: ['1 user', 'Unlimited projects', 'Full Kanban + Calendar'],
    limits: [
      'Task layout builder',
      'Custom fields, objects & automations',
      'Slack & Google Workspace apps',
      'Priority email support',
    ],
    teaser: [
      'Unlimited projects',
      'Full Kanban + Calendar',
      'Task layout builder & automations',
      'Slack & Google Workspace apps',
    ],
  },
  {
    id: 'team',
    name: 'Team',
    recommendedFor: 'Organizations & squads',
    priceMonthly: 5,
    pricePerUser: 3,
    minimumSeats: 1,
    billingInterval: 'month',
    ctaLabel: 'Choose Team',
    highlights: ['Everything in Starter', 'Per-user pricing', 'Shared boards & calendars'],
    limits: [
      'Customer Portal & Customer Billing',
      'User management (roles, invites)',
      'Project access controls',
      'Org-level settings',
      'Priority support',
    ],
    teaser: [
      'Everything in Starter',
      'Customer Portal & Billing',
      'Shared boards & calendars',
      'Roles, invites & org settings',
    ],
  },
];

export const WEBAPP_URL =
  process.env.REACT_APP_PABSYNC_WEBAPP_URL || 'https://app.pabsync.com';

/** Build the web-app registration URL for a plan (with optional seats). */
export const registerUrl = (
  planId: PlanId,
  opts?: { seats?: number }
): string => {
  const params = new URLSearchParams({ plan: planId, src: 'website' });
  if (opts?.seats) params.set('seats', String(opts.seats));
  return `${WEBAPP_URL}/register?${params.toString()}`;
};

export const formatMoney = (value: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);

export interface PriceDisplay {
  amount: string;
  unit: string;
}

/** Compact price for the homepage teaser cards. */
export const planPrice = (plan: Plan): PriceDisplay => {
  if (plan.id === 'team' && plan.pricePerUser) {
    return { amount: formatMoney(plan.pricePerUser), unit: '/ user / month' };
  }
  if (plan.priceMonthly === 0) return { amount: formatMoney(0), unit: 'forever' };
  return { amount: formatMoney(plan.priceMonthly), unit: '/ month' };
};
